import { useEffect, useState } from "react";
import { api, monthName } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, PageBody, Section } from "../components/layout/Page";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Star } from "@phosphor-icons/react";
import { toast } from "sonner";

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const YEARS = [2024, 2025, 2026];

export default function Performance() {
  const { user } = useAuth();
  const { activeCompanyId, activeCompany } = useCompany();
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", review_month: new Date().getMonth() + 1, review_year: new Date().getFullYear(), rating: 4, kpi_score: 80, manager_feedback: "", hr_remarks: "" });

  const canEdit = ["super_admin", "hr_admin", "manager"].includes(user?.role);

  const load = async () => {
    if (!activeCompanyId) return;
    const [r, e] = await Promise.all([
      api.get("/performance", { params: { company_id: activeCompanyId } }),
      api.get("/employees", { params: { company_id: activeCompanyId, limit: 500 } }),
    ]);
    setReviews(r.data); setEmployees(e.data.items);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const submit = async () => {
    try {
      await api.post("/performance", { ...form, company_id: activeCompanyId, rating: parseFloat(form.rating), kpi_score: parseFloat(form.kpi_score) });
      toast.success("Review created"); setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e]));

  return (
    <div data-testid="performance-page">
      <PageHeader eyebrow={activeCompany?.code} title="Performance" subtitle="Track reviews, ratings and KPIs.">
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-xs uppercase tracking-wider" data-testid="add-review-btn">
                <Plus size={14} className="mr-1.5" /> New review
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle>New performance review</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs uppercase">Employee</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({...form, employee_id: v})}>
                    <SelectTrigger className="rounded-sm" data-testid="review-emp"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase">Month</Label>
                    <Select value={String(form.review_month)} onValueChange={(v) => setForm({...form, review_month: Number(v)})}>
                      <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={String(m)}>{monthName(m)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs uppercase">Year</Label>
                    <Select value={String(form.review_year)} onValueChange={(v) => setForm({...form, review_year: Number(v)})}>
                      <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase">Rating (1-5)</Label><Input type="number" step="0.1" min="1" max="5" value={form.rating} onChange={(e) => setForm({...form, rating: e.target.value})} className="rounded-sm" /></div>
                  <div><Label className="text-xs uppercase">KPI score (0-100)</Label><Input type="number" min="0" max="100" value={form.kpi_score} onChange={(e) => setForm({...form, kpi_score: e.target.value})} className="rounded-sm" /></div>
                </div>
                <div><Label className="text-xs uppercase">Manager feedback</Label><Textarea rows={2} value={form.manager_feedback} onChange={(e) => setForm({...form, manager_feedback: e.target.value})} className="rounded-sm" /></div>
                <div><Label className="text-xs uppercase">HR remarks</Label><Textarea rows={2} value={form.hr_remarks} onChange={(e) => setForm({...form, hr_remarks: e.target.value})} className="rounded-sm" /></div>
              </div>
              <DialogFooter><Button onClick={submit} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="submit-review">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>
      <PageBody>
        <Section title={`${reviews.length} reviews`} dense>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Employee</TableHead><TableHead>Period</TableHead><TableHead>Rating</TableHead>
              <TableHead>KPI</TableHead><TableHead>Manager feedback</TableHead><TableHead>HR remarks</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {reviews.map((r) => {
                const e = empMap[r.employee_id] || {};
                return (
                  <TableRow key={r.id} data-testid={`review-${r.id}`}>
                    <TableCell>{e.full_name || "—"} <span className="text-xs font-mono text-[#64748B] ml-1">{e.employee_code}</span></TableCell>
                    <TableCell className="text-xs">{monthName(r.review_month)} {r.review_year}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-[#FAB005]">
                        <Star size={14} weight="fill" />
                        <span className="font-mono text-xs">{r.rating?.toFixed(1)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.kpi_score}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate">{r.manager_feedback}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate">{r.hr_remarks}</TableCell>
                  </TableRow>
                );
              })}
              {reviews.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#64748B] py-8">No reviews yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Section>
      </PageBody>
    </div>
  );
}
